import React, {createContext, useEffect, useReducer} from 'react';
import jwtDecode from 'jwt-decode';
import SplashScreen from 'src/components/SplashScreen';
import {default as axios} from '../utils/axios';

const initialAuthState = {
    isAuthenticated: false,
    isInitialised: false,
    user: null,
};

const isValidToken = accessToken => {
    if (!accessToken) {
        return false;
    }

    const decoded = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
};

const setSession = accessToken => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
        localStorage.removeItem('accessToken');
        delete axios.defaults.headers.common.Authorization;
    }
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'INITIALISE': {
            const {isAuthenticated, user} = action.payload;

            return {
                ...state,
                isAuthenticated,
                isInitialised: true,
                user,
            };
        }
        case 'LOGIN': {
            const {user} = action.payload;

            return {
                ...state,
                isAuthenticated: true,
                user,
            };
        }
        case 'UPDATE': {
            const {user} = action.payload;

            return {
                ...state,
                user,
            };
        }
        case 'LOGOUT': {
            return {
                ...state,
                isAuthenticated: false,
                user: null,
            };
        }
        case 'REGISTER': {
            const {user} = action.payload;

            return {
                ...state,
                isAuthenticated: true,
                user,
            };
        }
        default: {
            return {...state};
        }
    }
};

const AuthContext = createContext({
    ...initialAuthState,
    method: 'JWT',
    login: () => Promise.resolve(),
    logout: () => {},
    register: () => Promise.resolve(),
});

const getUserData = async token => {
    const jwtDecoded = jwtDecode(token);
    const {data} = await axios.get(`/api/users/${jwtDecoded.user_id}/`);
    return data;
};

export const AuthProvider = ({children}) => {
    const [state, dispatch] = useReducer(reducer, initialAuthState);

    const login = async (username, password) => {
        const response = await axios.post('/api/auth/token', {username, password});
        const {token} = response.data;
        setSession(token);
        //Get user data
        const user = await getUserData(token);
        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        });
    };

    const logout = () => {
        setSession(null);
        dispatch({type: 'LOGOUT'});
    };

    const register = async (email, name, password) => {
        const response = await axios.post('/api/account/register/', {
            email,
            name,
            password,
        });
        const {accessToken, user} = response.data;

        window.localStorage.setItem('accessToken', accessToken);

        dispatch({
            type: 'REGISTER',
            payload: {
                user,
            },
        });
    };

    const update = async values => {
        const response = await axios.put(`/api/users/${values.id}/`, values);
        const user = response.data;

        dispatch({
            type: 'UPDATE',
            payload: {
                user,
            },
        });
    };

    useEffect(() => {
        const initialise = async () => {
            try {
                const accessToken = window.localStorage.getItem('accessToken');

                if (accessToken && isValidToken(accessToken)) {
                    setSession(accessToken);
                    const response = await axios.post('/api/auth/token/refresh', {
                        token: accessToken,
                    });

                    setSession(response.data.token);

                    const user = await getUserData(response.data.token);
                    dispatch({
                        type: 'INITIALISE',
                        payload: {
                            isAuthenticated: true,
                            user,
                        },
                    });
                } else {
                    dispatch({
                        type: 'INITIALISE',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    });
                }
            } catch (err) {
                console.error(err);
                dispatch({
                    type: 'INITIALISE',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                });
            }
        };

        initialise();
    }, []);

    if (!state.isInitialised) {
        return <SplashScreen />;
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'JWT',
                login,
                logout,
                register,
                update,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
